/**
 * Category-keyed "instant shop" templates used by the simplified onboarding
 * flow. When a brand-new provider picks a category, the platform scaffolds a
 * DRAFT profile from one of these templates so the provider lands on an
 * already-populated dashboard instead of an empty form.
 *
 * Each template is matched to a backend Category by `code` (see
 * ReferenceDataSeeder.java — HAIR, CAKE, PASTRY, ...). Multiple seeds per
 * field keep auto-generated shops from looking identical when many providers
 * sign up in the same category.
 *
 * No backend change is required: scaffolding is implemented as a sequence of
 * the existing /providers/me + /providers/me/services calls. A future backend
 * scaffold endpoint can replace `scaffoldFromTemplate` without touching the
 * UI.
 */
import type { CategorySlug } from '../../shared/assets/categoryImages';

export type PriceType = 'FIXED' | 'STARTING_FROM';

export interface SuggestedService {
  /** Provider-facing name (matches an entry in the seeded service catalog
   *  when possible — e.g. "Box braids", "Wedding cake"). Free-text on the
   *  backend, so close variants are also fine. */
  name: string;
  /** Default price in minor units (cents) for the picked currency. Set to
   *  `undefined` when the service is too variable to suggest a price (the
   *  provider then fills it in from the dashboard). */
  priceCents?: number;
  priceType?: PriceType;
  durationMinutes?: number;
}

export interface ShopTemplate {
  /** Backend Category.code this template targets (HAIR, CAKE, PASTRY, ...). */
  categoryCode: string;
  /** Image folder slug used to pick a category-matched cover. */
  imageSlug: CategorySlug;
  /** Pool of business-name seeds. `{city}` is replaced by the picked city
   *  when present, then the placeholder is dropped if no city is known. */
  businessNameSeeds: string[];
  /** Pool of bio paragraphs (each ≥ 20 chars to satisfy publish validation).
   *  `{city}` is interpolated the same way as in business names. */
  bioSeeds: string[];
  /** Services pre-added to the DRAFT. Backend requires `categoryId` rather
   *  than `categoryCode`, so the caller resolves the ID from the seeded
   *  categories list and passes it through. */
  services: SuggestedService[];
}

/**
 * EUR-denominated suggested prices. The simplified onboarding always submits
 * in EUR; the provider can switch currency per service from the dashboard.
 */
export const TEMPLATE_CURRENCY = 'EUR';

/**
 * Templates keyed by backend Category.code. Add a new entry when a new
 * category is seeded server-side. Entries with an unknown code fall through
 * to {@link DEFAULT_TEMPLATE} so the flow never blocks on an unmapped code.
 */
const TEMPLATES: Record<string, ShopTemplate> = {
  HAIR: {
    categoryCode: 'HAIR',
    imageSlug: 'beautyHair',
    businessNameSeeds: [
      'Studio Tresses {city}',
      'Maison Coiffure Afro {city}',
      'Royal Braids {city}',
      'Afro Hair Studio {city}',
      'Tresses & Beauté {city}',
    ],
    bioSeeds: [
      "Spécialiste des tresses africaines et de la coiffure protectrice. Plus de 5 ans d'expérience pour sublimer vos cheveux dans une ambiance chaleureuse.",
      "Tresses, locs, nattes et soins du cheveu afro — un savoir-faire qui met vos cheveux naturels à l'honneur.",
      'Coiffure afro sur-mesure : box braids, knotless, nattes collées, pose de tissage et perruque. Sur rendez-vous à {city} et à domicile.',
    ],
    services: [
      { name: 'Box braids', priceCents: 8000, priceType: 'STARTING_FROM', durationMinutes: 240 },
      {
        name: 'Knotless braids',
        priceCents: 12000,
        priceType: 'STARTING_FROM',
        durationMinutes: 300,
      },
      { name: 'Cornrows', priceCents: 4000, priceType: 'STARTING_FROM', durationMinutes: 120 },
      {
        name: 'Pose de tissage',
        priceCents: 10000,
        priceType: 'STARTING_FROM',
        durationMinutes: 180,
      },
    ],
  },

  CAKE: {
    // Despite the legacy code, this category covers savoury Food & Catering.
    categoryCode: 'CAKE',
    imageSlug: 'foodCatering',
    businessNameSeeds: [
      'Saveurs d’Afrique {city}',
      'Le Goût du Pays {city}',
      'Cuisine Mama {city}',
      'Afro Traiteur {city}',
      'Délices d’Ici et d’Ailleurs {city}',
    ],
    bioSeeds: [
      'Cuisine africaine authentique — plats traditionnels, buffets et menus sur mesure pour vos événements. Livraison et service traiteur à {city}.',
      "Spécialités camerounaises, ivoiriennes et sénégalaises préparées à partir d'ingrédients frais. Réservez pour mariages, baptêmes ou repas en famille.",
      'Traiteur africain : du plat individuel au buffet pour 200 personnes. Saveurs maison, présentation soignée.',
    ],
    services: [
      { name: 'Repas individuel', priceCents: 1500, priceType: 'STARTING_FROM' },
      { name: 'Buffet africain', priceCents: 2500, priceType: 'STARTING_FROM' },
      { name: 'Plat traditionnel sur commande', priceCents: 2000, priceType: 'STARTING_FROM' },
    ],
  },

  PASTRY: {
    categoryCode: 'PASTRY',
    imageSlug: 'pastry',
    businessNameSeeds: [
      'Sweet Afro {city}',
      'Maison Pâtisserie {city}',
      'Gâteaux d’Or {city}',
      'Délices Sucrés {city}',
      'Wedding Cake Studio {city}',
    ],
    bioSeeds: [
      'Gâteaux personnalisés pour mariages, anniversaires et baptêmes. Décors sur mesure, parfums variés et finitions soignées.',
      'Pâtisserie créative à {city} : pièce montée, cupcakes, gâteaux à thème, desserts traditionnels africains revisités.',
      "Wedding cakes haute couture et pâtisseries d'inspiration africaine. Sur commande, livraison sur toute la région.",
    ],
    services: [
      { name: 'Gâteau d’anniversaire', priceCents: 6000, priceType: 'STARTING_FROM' },
      { name: 'Wedding cake', priceCents: 25000, priceType: 'STARTING_FROM' },
      { name: 'Cupcakes (la douzaine)', priceCents: 2500, priceType: 'FIXED' },
    ],
  },

  MEDIA_PHOTO_ANIMATION: {
    categoryCode: 'MEDIA_PHOTO_ANIMATION',
    imageSlug: 'mediaPhotoAnimation',
    businessNameSeeds: [
      'Studio Lumière {city}',
      'Cadre & Mémoire {city}',
      'Afro Frames {city}',
      'Photographe Événement {city}',
      'Visions {city}',
    ],
    bioSeeds: [
      'Photographe & vidéaste spécialisé(e) dans les événements afro et multiculturels. Mariages, baptêmes, anniversaires — vos plus beaux souvenirs immortalisés.',
      'Photographie portrait, mariage et lifestyle. Reportages complets, livraison rapide, retouches incluses.',
      'Captation vidéo, photo de mariage et création de contenu pour les marques afro émergentes à {city}.',
    ],
    services: [
      { name: 'Reportage mariage', priceCents: 80000, priceType: 'STARTING_FROM' },
      { name: 'Séance portrait', priceCents: 15000, priceType: 'STARTING_FROM' },
      { name: 'Captation vidéo événement', priceCents: 50000, priceType: 'STARTING_FROM' },
    ],
  },

  ARTISTS_PERFORMANCE: {
    categoryCode: 'ARTISTS_PERFORMANCE',
    imageSlug: 'artistsPerformance',
    businessNameSeeds: [
      'DJ Afro Vibes {city}',
      'Studio Artiste {city}',
      'Performance Africaine {city}',
      'Scène & Rythmes {city}',
      'Sound of Africa {city}',
    ],
    bioSeeds: [
      'DJ et animateur événementiel — mariages, anniversaires, soirées privées. Playlist afro, coupé-décalé, R&B, hits internationaux.',
      'Danseur(se) et performeur(se) — chorégraphies africaines traditionnelles et modernes pour vos événements à {city}.',
      'MC, animateur(trice) et artiste de scène. Ambiance garantie pour tout type de cérémonie.',
    ],
    services: [
      { name: 'Animation DJ (4h)', priceCents: 35000, priceType: 'STARTING_FROM' },
      { name: 'Animation MC', priceCents: 25000, priceType: 'STARTING_FROM' },
      { name: 'Spectacle de danse', priceCents: 40000, priceType: 'STARTING_FROM' },
    ],
  },

  EVENT_SERVICES: {
    categoryCode: 'EVENT_SERVICES',
    imageSlug: 'eventServices',
    businessNameSeeds: [
      'Royal Events {city}',
      'Décoration Africaine {city}',
      'Mémoires & Cérémonies {city}',
      'Afro Wedding Planner {city}',
      'L’Atelier Événementiel {city}',
    ],
    bioSeeds: [
      'Wedding planner et décoration événementielle — mariages traditionnels, baptêmes, anniversaires. Mise en scène sur mesure, fleurs et tissus africains.',
      'Location de matériel, décoration de salle et coordination jour J. {city} et région.',
      "Organisation complète d'événements multiculturels : du concept à la coordination, en passant par la location et la décoration.",
    ],
    services: [
      { name: 'Décoration de salle', priceCents: 50000, priceType: 'STARTING_FROM' },
      { name: 'Wedding planning complet', priceCents: 150000, priceType: 'STARTING_FROM' },
      { name: 'Location matériel événement', priceCents: 30000, priceType: 'STARTING_FROM' },
    ],
  },

  FASHION_STYLE: {
    categoryCode: 'FASHION_STYLE',
    imageSlug: 'fashionStyle',
    businessNameSeeds: [
      'Couture Afro {city}',
      'Maison Tissu {city}',
      'Atelier Style {city}',
      'Wax & Élégance {city}',
      'Sur Mesure {city}',
    ],
    bioSeeds: [
      'Créateur(trice) de mode afro — tenues sur mesure pour cérémonies, mariages et tous les jours. Wax, bazin, pagne et étoffes nobles.',
      "Couture traditionnelle et contemporaine, robes de mariée africaines, tenues d'invités. Sur rendez-vous à {city}.",
      'Styliste — accompagnement personnalisé, retouches, créations exclusives et accessoires assortis.',
    ],
    services: [
      { name: 'Robe sur mesure', priceCents: 18000, priceType: 'STARTING_FROM' },
      { name: 'Tenue de mariage', priceCents: 50000, priceType: 'STARTING_FROM' },
      { name: 'Retouches', priceCents: 3000, priceType: 'STARTING_FROM' },
    ],
  },

  ADDITIONAL_SERVICES: {
    categoryCode: 'ADDITIONAL_SERVICES',
    imageSlug: 'additionalServices',
    businessNameSeeds: [
      'Services Pro {city}',
      'Afro Experts {city}',
      'Multi-Services {city}',
      'L’Atelier {city}',
    ],
    bioSeeds: [
      'Services professionnels personnalisés pour la communauté afro à {city}. Contactez-moi pour discuter de vos besoins.',
      'Prestations sur mesure pour particuliers et entreprises. Devis gratuit, prestation soignée, satisfaction garantie.',
    ],
    services: [{ name: 'Consultation initiale', priceCents: 5000, priceType: 'STARTING_FROM' }],
  },
};

const DEFAULT_TEMPLATE: ShopTemplate = TEMPLATES.ADDITIONAL_SERVICES;

/** Resolve a template by backend Category.code (case-insensitive). */
export function templateForCategoryCode(code: string | null | undefined): ShopTemplate {
  if (!code) return DEFAULT_TEMPLATE;
  return TEMPLATES[code.toUpperCase()] ?? DEFAULT_TEMPLATE;
}

/** Concrete scaffold values picked from a template's seed pools. */
export interface ScaffoldValues {
  businessName: string;
  bio: string;
  services: SuggestedService[];
}

/**
 * Pick one concrete (businessName, bio) pair plus the full service list.
 * Randomised so two providers in the same category in the same city don't
 * end up with identical shops. Pass {@code seed} for deterministic output
 * (e.g. in tests).
 */
export function scaffoldFromTemplate(
  template: ShopTemplate,
  ctx: { city?: string; seed?: number } = {},
): ScaffoldValues {
  const seed = ctx.seed ?? Math.floor(Math.random() * 1_000_000);
  const interpolate = (s: string) =>
    ctx.city
      ? s.replace(/\s*\{city\}/g, ` ${ctx.city}`).trim()
      : s.replace(/\s*\{city\}/g, '').trim();

  const name = template.businessNameSeeds[seed % template.businessNameSeeds.length];
  const bio = template.bioSeeds[(seed >> 1) % template.bioSeeds.length];

  return {
    businessName: interpolate(name),
    bio: interpolate(bio),
    services: template.services,
  };
}
